import {Drawer} from '@mui/material';
import {ARIA_DRAWER_WIDTH_CSS_VAR} from '@wandb/forge-shell-contract';
import React, {useEffect} from 'react';

import {useWorkspaceDrawerTop} from '../MultiRunWorkspace/useWorkspaceDrawerTop';
import {
  INITIAL_DRAWER_WIDTH,
  INLINE_WORKSPACE_DRAWER_DURATION,
} from './constants';

/**
 * A custom hook that manages transition events for the floating workspace drawer.
 * It adds and removes event listeners for drawer transitions, allowing components
 * to react to drawer opening/closing animations.
 *
 * Example: you want to scroll to an item inside the drawer when it's opened.
 *
 * @param transition - The transition event to listen for (e.g. 'transitionend')
 * @param callback - Function to execute when the transition event occurs
 */
export const useFloatingWorkspaceDrawerTransition = (
  transition: string,
  callback: () => void
) => {
  useEffect(() => {
    const drawer = document.getElementById('floating-workspace-drawer');
    drawer?.addEventListener(transition, callback);
    return () => {
      const drawer = document.getElementById('floating-workspace-drawer');
      drawer?.removeEventListener(transition, callback);
    };
  }, [callback, transition]);
};

export const FloatingWorkspaceDrawer = ({
  isDrawerOpen,
  closeWorkspaceDrawer,
  children,
}: {
  isDrawerOpen: boolean;
  closeWorkspaceDrawer: () => void;
  children: React.ReactNode;
}) => {
  const marginTop = useWorkspaceDrawerTop();

  return (
    <Drawer
      anchor="right"
      id="floating-workspace-drawer"
      open={isDrawerOpen}
      onClose={closeWorkspaceDrawer}
      variant="persistent" // renders drawer asChild
      transitionDuration={INLINE_WORKSPACE_DRAWER_DURATION} // for consistency, make sure this matches inline drawer duration
      slotProps={{
        paper: {
          style: {
            marginTop,
            right: `var(${ARIA_DRAWER_WIDTH_CSS_VAR}, 0px)`,
            height: `calc(100% - ${marginTop})`,
            boxShadow: 'var(--wb-shadow-md)',
            width: INITIAL_DRAWER_WIDTH,
          },
        },
      }}>
      {children}
    </Drawer>
  );
};
